import { ReactNode } from 'react'
import { Button } from '@/components/ui/Button'

export interface ZeroStateProps {
  /**
   * Icon or illustration to display
   */
  icon: ReactNode

  /**
   * Title text
   */
  title: string

  /**
   * Description text
   */
  description: string

  /**
   * Optional action button
   */
  action?: {
    label: string
    icon?: ReactNode
    onClick: () => void
  }

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * ZeroState Component
 *
 * A pattern component for displaying empty states with an optional action button.
 *
 * @example
 * <ZeroState
 *   icon={<FileOutlined style={{ fontSize: 64 }} />}
 *   title="No Attendees Yet"
 *   description="Get started by adding your first attendee to the event."
 *   action={{
 *     label: "Add Attendee",
 *     icon: <PlusOutlined />,
 *     onClick: handleAddAttendee
 *   }}
 * />
 *
 * @example
 * // Zero state without action
 * <ZeroState
 *   icon={<InboxOutlined />}
 *   title="No Data Available"
 *   description="There is no data to display at this time."
 * />
 */
export function ZeroState({ icon, title, description, action, className = '' }: ZeroStateProps) {
  return (
    <div className={`w-full flex flex-col items-center justify-center py-16 px-8 text-center ${className}`}>
      <div className="mb-6 text-gray-400 text-6xl">{icon}</div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-sm text-gray-500 mb-6" style={{ maxWidth: '28rem' }}>{description}</p>
      {action && (
        <Button variant="primary" size="small" icon={action.icon} onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  )
}
